import { marked } from 'marked'
import type { HistoryEntry, KeyboardShortcut } from '@repo/commons/models'
import type { TempUploadedFileResponse } from '@semanticedge/knowledge-service-noheaders-client/src/models/index.js'

export const HISTORY_LIMIT = 100
export const SAVE_DELAY = 100

export interface ImageUploadResponse extends TempUploadedFileResponse {}

export interface ImageUploadResult {
	url: string
	id: string
	file: File
}

export function saveToHistory(
	history: HistoryEntry[],
	historyIndex: number,
	value: string,
	textareaRef?: HTMLTextAreaElement
): { newHistory: HistoryEntry[]; newIndex: number } {
	if (!textareaRef) return { newHistory: history, newIndex: historyIndex }

	const currentEntry: HistoryEntry = {
		content: value,
		selectionStart: textareaRef.selectionStart,
		selectionEnd: textareaRef.selectionEnd,
		timestamp: Date.now()
	}

	if (history.length > 0 && history[historyIndex]?.content === value) {
		return { newHistory: history, newIndex: historyIndex }
	}

	let newHistory = historyIndex < history.length - 1 ? history.slice(0, historyIndex + 1) : history
	newHistory = [...newHistory, currentEntry]

	if (newHistory.length > HISTORY_LIMIT) {
		newHistory = newHistory.slice(-HISTORY_LIMIT)
	}

	return { newHistory, newIndex: newHistory.length - 1 }
}

export function undoHistory(
	history: HistoryEntry[],
	historyIndex: number,
	textareaRef?: HTMLTextAreaElement
): { newIndex: number; entry?: HistoryEntry } {
	if (historyIndex <= 0 || !textareaRef) {
		return { newIndex: historyIndex }
	}

	const newIndex = historyIndex - 1
	const entry = history[newIndex]

	return { newIndex, entry }
}

export function redoHistory(
	history: HistoryEntry[],
	historyIndex: number,
	textareaRef?: HTMLTextAreaElement
): { newIndex: number; entry?: HistoryEntry } {
	if (historyIndex >= history.length - 1 || !textareaRef) {
		return { newIndex: historyIndex }
	}

	const newIndex = historyIndex + 1
	const entry = history[newIndex]

	return { newIndex, entry }
}

export function restoreCursorPosition(
	textareaRef: HTMLTextAreaElement,
	selectionStart: number,
	selectionEnd: number
): void {
	const scrollTop = textareaRef.scrollTop // Preserve scroll position

	requestAnimationFrame(() => {
		textareaRef.focus()
		textareaRef.setSelectionRange(selectionStart, selectionEnd)
		textareaRef.scrollTop = scrollTop // Restore scroll position
	})
}

export function insertNumberedListItem(
	value: string,
	textareaRef: HTMLTextAreaElement
): { newValue: string; cursorPosition: number } {
	const cursorPos = textareaRef.selectionStart
	const lines = value.split('\n')

	// Find which line the cursor is on
	let currentLineIndex = 0
	let charCount = 0

	for (let i = 0; i < lines.length; i++) {
		if (charCount + lines[i].length >= cursorPos) {
			currentLineIndex = i
			break
		}
		charCount += lines[i].length + 1 // +1 for newline
	}

	const currentLine = lines[currentLineIndex]
	const trimmedLine = currentLine.trim()

	// Check if current line is already a numbered list item
	const numberedListMatch = trimmedLine.match(/^(\d+)\.\s/)

	if (numberedListMatch) {
		// We're in a numbered list, add the next item
		const currentNumber = parseInt(numberedListMatch[1])
		const nextNumber = currentNumber + 1
		const newListItem = `${nextNumber}. `

		// Insert after current line
		lines.splice(currentLineIndex + 1, 0, newListItem)

		const newValue = lines.join('\n')
		const newCursorPos =
			value.substring(0, value.indexOf(currentLine) + currentLine.length).length +
			1 +
			newListItem.length

		return { newValue, cursorPosition: newCursorPos }
	} else {
		// Not in a numbered list, start a new one
		const indent = currentLine.match(/^\s*/)?.[0] || ''
		const newListItem = `${indent}1. `

		if (trimmedLine === '') {
			// Empty line, replace with list item
			lines[currentLineIndex] = newListItem
		} else {
			// Non-empty line, insert new list item after
			lines.splice(currentLineIndex + 1, 0, newListItem)
		}

		const newValue = lines.join('\n')

		// Calculate new cursor position
		let newCursorPos = 0
		for (let i = 0; i <= currentLineIndex; i++) {
			if (i === currentLineIndex && trimmedLine === '') {
				newCursorPos += newListItem.length
			} else if (i === currentLineIndex) {
				newCursorPos += lines[i].length + 1 + newListItem.length
			} else {
				newCursorPos += lines[i].length + 1
			}
		}

		return { newValue, cursorPosition: newCursorPos }
	}
}

export function insertBulletListItem(
	value: string,
	textareaRef: HTMLTextAreaElement
): { newValue: string; cursorPosition: number } {
	const cursorPos = textareaRef.selectionStart
	const lines = value.split('\n')

	// Find which line the cursor is on
	let currentLineIndex = 0
	let charCount = 0

	for (let i = 0; i < lines.length; i++) {
		if (charCount + lines[i].length >= cursorPos) {
			currentLineIndex = i
			break
		}
		charCount += lines[i].length + 1 // +1 for newline
	}

	const currentLine = lines[currentLineIndex]
	const trimmedLine = currentLine.trim()

	// Check if current line is already a bullet list item
	const bulletListMatch = trimmedLine.match(/^[-*+]\s/)

	if (bulletListMatch) {
		// We're in a bullet list, add the next item
		const bulletChar = bulletListMatch[0].charAt(0)
		const newListItem = `${bulletChar} `

		// Insert after current line
		lines.splice(currentLineIndex + 1, 0, newListItem)

		const newValue = lines.join('\n')
		const newCursorPos =
			value.substring(0, value.indexOf(currentLine) + currentLine.length).length +
			1 +
			newListItem.length

		return { newValue, cursorPosition: newCursorPos }
	} else {
		// Not in a bullet list, start a new one
		const indent = currentLine.match(/^\s*/)?.[0] || ''
		const newListItem = `${indent}- `

		if (trimmedLine === '') {
			// Empty line, replace with list item
			lines[currentLineIndex] = newListItem
		} else {
			// Non-empty line, insert new list item after
			lines.splice(currentLineIndex + 1, 0, newListItem)
		}

		const newValue = lines.join('\n')

		// Calculate new cursor position
		let newCursorPos = 0
		for (let i = 0; i <= currentLineIndex; i++) {
			if (i === currentLineIndex && trimmedLine === '') {
				newCursorPos += newListItem.length
			} else if (i === currentLineIndex) {
				newCursorPos += lines[i].length + 1 + newListItem.length
			} else {
				newCursorPos += lines[i].length + 1
			}
		}

		return { newValue, cursorPosition: newCursorPos }
	}
}

export async function uploadImageToMinIO(file: File): Promise<ImageUploadResult> {
	try {
		// Validate file before upload
		const validation = validateImageFile(
			file,
			['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
			32
		)
		if (!validation.isValid) {
			throw new Error(validation.error || 'Invalid file')
		}

		// Make API request with timeout
		const controller = new AbortController()
		const timeoutId = setTimeout(() => controller.abort(), 30_000) // 30 second timeout

		const formData = new FormData()
		formData.append('file', file)

		const response = await fetch('/api/shared/knowledge/upload-temp', {
			method: 'POST',
			signal: controller.signal,
			body: formData
		})

		clearTimeout(timeoutId)

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`)
		}

		const data: ImageUploadResponse = await response.json()

		if (!data.url) {
			throw new Error('Upload failed')
		}

		console.log('data', data)

		return {
			file,
			url: data.url || '',
			id: data.fileIdentifier || ''
		}
	} catch (error) {
		if (error instanceof Error) {
			if (error.name === 'AbortError') {
				throw new Error('Upload timeout - please try again')
			}
			throw error
		}
		throw new Error('An unexpected error occurred during upload')
	}
}

// Create preview URL for immediate display
export function createPreviewUrl(file: File): string {
	return URL.createObjectURL(file)
}

export function revokePreviewUrl(url: string): void {
	if (url.startsWith('blob:')) {
		URL.revokeObjectURL(url)
	}
}

export function validateImageFile(
	file: File,
	acceptedTypes: string[],
	maxSizeMB: number
): { isValid: boolean; error?: string } {
	// Check file type
	if (!acceptedTypes.includes(file.type)) {
		const acceptedExtensions = acceptedTypes.map((type) => type.split('/')[1]).join(', ')
		return {
			isValid: false,
			error: `Invalid file type. Accepted formats: ${acceptedExtensions}`
		}
	}

	// Check file size
	const maxSizeBytes = maxSizeMB * 1024 * 1024
	if (file.size > maxSizeBytes) {
		return {
			isValid: false,
			error: `File too large. Maximum size: ${maxSizeMB}MB`
		}
	}

	// Check if file is actually an image
	if (!file.type.startsWith('image/')) {
		return {
			isValid: false,
			error: 'File must be an image'
		}
	}

	return { isValid: true }
}

export function createImageMarkdown(fileName: string, url: string): string {
	return `![${fileName}](${url})\n\n`
}

// Utility to escape special regex characters
function escapeRegExp(string: string): string {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Parse markdown table
interface TableData {
	headers: string[]
	rows: string[][]
	alignment?: string[]
}

function parseMarkdownTable(tableText: string): TableData | null {
	const lines = tableText.trim().split('\n')
	if (lines.length < 2) return null

	const headerLine = lines[0].trim()
	const separatorLine = lines[1].trim()

	// Check if separator line is valid
	if (!separatorLine.match(/^\|?[\s|:\-]+\|[\s|:\-]+\|?$/)) {
		return null
	}

	const headers = headerLine
		.split('|')
		.map((h) => h.trim())
		.filter((h) => h.length > 0)

	// Extract alignment from separator
	const alignment = separatorLine
		.split('|')
		.map((cell) => {
			const trimmed = cell.trim()
			if (trimmed.startsWith(':') && trimmed.endsWith(':')) return 'center'
			if (trimmed.endsWith(':')) return 'right'
			if (trimmed.startsWith(':')) return 'left'
			return 'left'
		})
		.filter((_, i) => i > 0 && i <= headers.length)

	const rows: string[][] = []
	for (let i = 2; i < lines.length; i++) {
		const rowLine = lines[i].trim()
		if (!rowLine) continue

		const cells = rowLine
			.split('|')
			.map((cell) => cell.trim())
			.filter((_, i) => i > 0)
			.slice(0, headers.length)

		if (cells.length > 0) {
			rows.push(cells)
		}
	}

	return { headers, rows, alignment }
}

function renderTable(tableData: TableData): string {
	const { headers, rows, alignment } = tableData

	let html = '<div class="table-wrapper"><table class="markdown-table">'

	// Render header
	html += '<thead><tr>'
	headers.forEach((header, i) => {
		const align = alignment?.[i] || 'left'
		html += `<th style="text-align: ${align};">${escapeHtml(header)}</th>`
	})
	html += '</tr></thead>'

	// Render rows
	html += '<tbody>'
	rows.forEach((row) => {
		html += '<tr>'
		row.forEach((cell, i) => {
			const align = alignment?.[i] || 'left'
			html += `<td style="text-align: ${align};">${escapeHtml(cell)}</td>`
		})
		html += '</tr>'
	})
	html += '</tbody></table></div>'

	return html
}

function escapeHtml(text: string): string {
	const map: { [key: string]: string } = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		"'": '&#039;'
	}
	return text.replace(/[&<>"']/g, (char) => map[char])
}

export function renderMarkdown(text: string): string {
	if (typeof text !== 'string') return ''

	const html = marked.parse(text, { async: false })
	return typeof html === 'string' ? html : String(html)
}

export function insertTextAtCursor(
	value: string,
	textareaRef: HTMLTextAreaElement,
	before: string,
	after: string = ''
): { newValue: string; selectionStart: number; selectionEnd: number } {
	const start = textareaRef.selectionStart
	const end = textareaRef.selectionEnd
	const selectedText = value.substring(start, end)

	const newValue = value.substring(0, start) + before + selectedText + after + value.substring(end)

	return {
		newValue,
		selectionStart: start + before.length,
		selectionEnd: start + before.length + selectedText.length
	}
}

export function autoResizeTextarea(textareaRef: HTMLTextAreaElement, maxHeight: string): void {
	const scrollTop = textareaRef.scrollTop // Preserve scroll position
	textareaRef.style.height = 'auto'
	textareaRef.style.height = Math.min(textareaRef.scrollHeight, parseInt(maxHeight)) + 'px'
	textareaRef.scrollTop = scrollTop // Restore scroll position
}

export function handleKeyboardShortcuts(
	event: KeyboardEvent,
	shortcuts: KeyboardShortcut[]
): boolean {
	for (const shortcut of shortcuts) {
		if (
			event.key === shortcut.key &&
			(!shortcut.ctrlKey || event.ctrlKey) &&
			(!shortcut.metaKey || event.metaKey) &&
			(!shortcut.shiftKey || event.shiftKey)
		) {
			event.preventDefault()
			shortcut.action()
			return true
		}
	}
	return false
}

export function createDebouncer(delay: number) {
	let timeoutId: number | undefined

	return function debounce(callback: () => void) {
		clearTimeout(timeoutId)
		timeoutId = setTimeout(callback, delay)
	}
}

// Parse the utterance and extract segments (text and placeholders)
export function parseUtterance(
	text: string
): Array<{ type: 'text' | 'placeholder'; content: string; key?: string }> {
	const segments: Array<{ type: 'text' | 'placeholder'; content: string; key?: string }> = []
	let lastIndex = 0

	// Find all placeholders like {{uuid}}
	const placeholderRegex = /\{\{([^}]+)\}\}/g
	let match

	while ((match = placeholderRegex.exec(text)) !== null) {
		const [fullMatch, key] = match

		// Add text before the placeholder
		if (match.index > lastIndex) {
			const textSegment = text.slice(lastIndex, match.index)
			if (textSegment.trim()) {
				segments.push({ type: 'text', content: textSegment })
			}
		}

		// Add placeholder
		segments.push({ type: 'placeholder', content: fullMatch, key })

		lastIndex = match.index + fullMatch.length
	}

	// Add remaining text
	if (lastIndex < text.length) {
		const remainingText = text.slice(lastIndex)
		if (remainingText.trim()) {
			segments.push({ type: 'text', content: remainingText })
		}
	}

	return segments
}
